import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'use-context-selector';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { isServer } from '@frameio/utils';
import type { SerializedPanelState } from '@frameio/vapor';
import { useIsAIPanelAvailable } from '../../ai/useIsAIPanelAvailable';
import type { PanelArea, PanelLayout, PanelsTemplate } from '../../contexts';
import { layouts, PanelLayoutContext } from '../../contexts';
import { useClientNameContext } from '../../contexts/ClientNameContext';
import type {
  PanelsBreakpointTemplate,
  PanelUnitProps,
} from '../../contexts/PanelLayoutContext/types';
import type { PanelLayoutState } from '..';
import { useTemplateLayoutState } from './useTemplateLayoutState';

const defineBreakpoint = (previous, latest) => {
  return {
    ...previous,
    columns: defineDefaults('column', previous.columns, latest?.columns),
    rows: defineDefaults('row', previous.rows, latest?.rows),
  };
};

const defineDefaults = (type, previous, latest) => {
  return previous.map((item, index) => {
    return {
      ...item,
      defaultSize: latest
        ? convertToPercentSize(type, item, latest[index])
        : convertToPixelsSize(type, item),
    };
  });
};

const convertToPixelsSize = (type, item) => {
  // Retain any defaults having a fraction (fr) unit:
  if (typeof item.defaultSize === 'string') return item.defaultSize;

  const screenSize = type === 'column' ? window.innerWidth : window.innerHeight;

  /**
   * No need to clamp range, we have already default max and mins defined
   * for each
   */
  return Math.round(item.defaultSize * screenSize);
};

const convertToPercentSize = (type, item, latestItem) => {
  // store all values in percentages.
  const size = latestItem?.size || item.defaultSize;

  // Retain any defaults having a fraction (fr) unit:
  if (typeof item.defaultSize === 'string') return item.defaultSize;

  const screenSize = type === 'column' ? window.innerWidth : window.innerHeight;
  const percentOfScreen = size / screenSize;

  /**
   * Store percentage of screen value as a number between 0-1. Round to 3
   * decimal points:
   */
  // @ts-ignore
  return +`${Math.round(`${percentOfScreen}e+3`)}e-3`;
};

/**
 * modify the given template in-place for integrations clients, see FCI-185
 */
const modifyTemplate = (
  template: PanelsTemplate,
  layoutKey: PanelLayout,
  clientName: ReturnType<typeof useClientNameContext>,
  phase?: string
) => {
  const update = (
    arr: Array<PanelUnitProps>,
    key: PanelArea,
    updater: (item: PanelUnitProps) => void
  ) => {
    const index = arr.findIndex((item) => item.key === key);
    updater(arr[index]);
  };

  if (
    clientName === 'web' &&
    ['viewer'].includes(layoutKey) &&
    !template.visible.includes('defaultRow')
  ) {
    const msg = 'V4BG-4023 issue detected';
    const viewport =
      typeof window === 'undefined'
        ? null
        : { height: window.innerHeight, width: window.innerWidth };
    datadogLogger.error(msg, { phase, template, viewport }, new Error(msg));
    template.visible.push('defaultRow');
  }

  // force the directoryNav to always be included
  if (!template.visible.includes('directoryNav')) {
    template.visible = [...template.visible, 'directoryNav']; // eslint-disable-line no-param-reassign
  }

  if (
    (clientName === 'premiere' || clientName === 'aftereffects') &&
    ['shareReviewViewer', 'viewer'].includes(layoutKey)
  ) {
    update(template.base.columns, 'directoryContent', (column) => {
      column.isToggleable = false; // eslint-disable-line no-param-reassign
    });
    update(template.base.rows, 'defaultRow', (row) => {
      row.isToggleable = true; // eslint-disable-line no-param-reassign
    });
    update(template.base.rows, 'details', (row) => {
      row.isToggleable = false; // eslint-disable-line no-param-reassign
    });
  }
};

/**
 * The AI panel's open state is shared across layouts for the session, so a
 * stored template's baked-in ai visibility must yield to the live state.
 * Otherwise navigating between layouts renders one frame from the stored
 * template, briefly unmounting an open panel (or flashing a closed one).
 */
const reconcileAiVisibility = (
  template: PanelsTemplate,
  isAiOpen: boolean
): PanelsTemplate => {
  const visible: PanelArea[] = (template.visible as PanelArea[]).filter(
    (area) => area !== 'ai'
  );
  if (isAiOpen) visible.push('ai');
  const overrideAiColumn = (breakpoint: PanelsBreakpointTemplate) => ({
    ...breakpoint,
    columns: breakpoint.columns.map((column) =>
      column.key === 'ai' && column.isToggleable !== false
        ? { ...column, showing: isAiOpen }
        : column
    ),
  });
  return {
    ...template,
    visible,
    base: overrideAiColumn(template.base),
    tablet: overrideAiColumn(template.tablet),
    desktop: overrideAiColumn(template.desktop),
  };
};

type UsePanelLayoutStateOpts = {
  /**
   * An optional way to disable storing the panel template, used primarily for
   * SSR pages
   */
  isStoredTemplateDisabled?: boolean;
};

export const usePanelLayoutState = (
  layout: PanelLayout,
  opts?: UsePanelLayoutStateOpts
): PanelLayoutState => {
  const clientName = useClientNameContext();

  const { isStoredTemplateDisabled } = opts || {};
  /**
   * Make sure not to update the state on first update.
   */
  const firstUpdate = useRef(true);

  /**
   * Use the storedTemplate unless the hook state has been updated.
   * To do this we need to maintain a ref of
   * what was updated most recently (store or local state)
   */
  const storedTemplateUpdated = useRef(false);

  const { visiblePanelsByLayout, setVisiblePanels } =
    useContext(PanelLayoutContext);
  const visiblePanels = visiblePanelsByLayout[layout];
  const stringifiedVisiblePanels = visiblePanels?.join(',');
  const panelLayoutState = layouts[layout]?.getPanelState?.(visiblePanels);

  const { setTemplate: setTemplateState, template } =
    useTemplateLayoutState(layout);

  const storedTemplate = template as PanelsTemplate;
  const stringifiedStoredVisiblePanels = storedTemplate?.visible.join(',');

  const storedTemplateLayout = useMemo(() => {
    // prevent server from calculating values on non-existent `window` object
    if (!template || isStoredTemplateDisabled || isServer) {
      modifyTemplate(
        panelLayoutState,
        layout,
        clientName,
        '1a. storedTemplateLayout case a'
      );
      return panelLayoutState;
    }

    const templateInPixels = {
      ...storedTemplate,
      desktop: defineBreakpoint(storedTemplate.desktop, null),
      tablet: defineBreakpoint(storedTemplate.tablet, null),
      base: defineBreakpoint(storedTemplate.base, null),
    };

    modifyTemplate(
      templateInPixels,
      layout,
      clientName,
      '1b. storedTemplateLayout case b'
    );

    storedTemplateUpdated.current = true;

    return reconcileAiVisibility(
      templateInPixels,
      Boolean((visiblePanels as PanelArea[] | undefined)?.includes('ai'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, layout, template]);

  const [templateLayout, setTemplateLayout] = useState(storedTemplateLayout);

  const setTemplate = (templateState: SerializedPanelState) => {
    // prevent server from calculating values on non-existent `window` object
    if (isStoredTemplateDisabled || isServer) return;

    const templateInPercents: PanelsTemplate = {
      ...panelLayoutState,
      desktop: defineBreakpoint(
        panelLayoutState.desktop,
        templateState.desktop
      ),
      tablet: defineBreakpoint(panelLayoutState.tablet, templateState.tablet),
      base: defineBreakpoint(panelLayoutState.base, templateState.base),
    };
    setTemplateState(templateInPercents);
  };

  /*
   * Current panel state has changed only after the initial update.
   * Update the templateLayout with a state change.
   */
  useEffect(() => {
    if (firstUpdate.current) return;

    storedTemplateUpdated.current = false;
    modifyTemplate(panelLayoutState, layout, clientName, '2. useEffect case');
    setTemplateLayout(panelLayoutState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, layout, visiblePanels]);

  // memoized helper for syncing visible panels
  const syncVisiblePanels = useCallback(() => {
    if (stringifiedStoredVisiblePanels !== stringifiedVisiblePanels) {
      // The AI panel's open state is shared across layouts for the current
      // session, so restoring a layout's stored template must not reopen or
      // close it.
      const nextVisible: PanelArea[] = (
        (storedTemplateLayout?.visible ?? []) as PanelArea[]
      ).filter((area) => area !== 'ai');
      if ((visiblePanels as PanelArea[] | undefined)?.includes('ai')) {
        nextVisible.push('ai');
      }
      setVisiblePanels(layout, nextVisible);
    }
  }, [
    layout,
    setVisiblePanels,
    storedTemplateLayout?.visible,
    stringifiedStoredVisiblePanels,
    stringifiedVisiblePanels,
    visiblePanels,
  ]);

  /*
   * Update the panels visibility if there is a mismatch from store initially.
   * we are comparing `storedTemplate`  === `visiblePanels`
   * Stored template is how we generate the initialTemplate in the useMemo.
   */
  useEffect(() => {
    if (isStoredTemplateDisabled) return;

    syncVisiblePanels();

    // The first update is now complete:
    firstUpdate.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Filter out flagged panels
   */
  const isAIEnabled = useIsAIPanelAvailable();
  useEffect(() => {
    if (isAIEnabled || !storedTemplateLayout?.visible.includes('ai')) return;
    const nextVisible = storedTemplateLayout?.visible.filter(
      (panel) => panel !== 'ai'
    ) as PanelArea[];
    setVisiblePanels(layout, nextVisible);
    if (!isStoredTemplateDisabled && !isServer) {
      const nextTemplate = layouts[layout].getPanelState(nextVisible);
      setTemplateState(nextTemplate);
      setTemplateLayout(nextTemplate);
      storedTemplateUpdated.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIEnabled]);

  if (isStoredTemplateDisabled) {
    return {
      template: panelLayoutState,
      setTemplate,
    };
  }

  return {
    template: storedTemplateUpdated.current
      ? storedTemplateLayout
      : templateLayout,
    setTemplate,
  };
};
